import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Eye, Loader2 } from "lucide-react";

import { Modal } from "../../../../shared/ui/modal";
import PatientFinder from "../../../../shared/ui/patient/PatientFinder";
import { useTherapist } from "../../../../context/useTherapist";
import { usePatientOptions } from "../../../patient";
import { useStartEmdrSession } from "../../application/useStartEmdrSession";

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

/**
 * What the EMDR entry of the home dock opens.
 *
 * The home has nobody in front of it, so the sheet asks who the session is
 * with — individual patients only, as the ratings are kept on one patient's
 * session — then starts a new session and opens its board in a new tab. A
 * patient's own dock needs no sheet: it starts straight away.
 */
export default function EmdrDockActions({ isOpen, onClose }: Props) {
    const { t } = useTranslation();
    const { therapist } = useTherapist();
    const { options, loading } = usePatientOptions(isOpen ? therapist?.id : undefined);
    const { start, starting, failed } = useStartEmdrSession();
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const patients = useMemo(() => options.map((option) => ({ id: option.value, name: option.label })), [options]);
    const selected = patients.find((patient) => patient.id === selectedId) ?? null;

    const close = () => {
        setSelectedId(null);
        onClose();
    };

    const begin = async () => {
        if (!selected) return;
        if (await start(selected)) close();
    };

    return (
        <Modal isOpen={isOpen} onClose={close} showCloseButton={false} className="mx-4 max-w-lg rounded-3xl">
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex max-h-[80vh] flex-col overflow-y-auto p-5"
                data-testid="emdr-dock-sheet"
            >
                <div className="mb-4">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">{t("emdr.dock.title")}</h2>
                    <p className="text-sm text-content-tertiary">{t("emdr.dock.subtitle")}</p>
                </div>

                {loading ? (
                    <p role="status" className="mb-4 flex items-center gap-2 text-sm text-content-tertiary">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        {t("emdr.dock.loading")}
                    </p>
                ) : (
                    <div className="mb-4">
                        <PatientFinder
                            patients={patients}
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                            disabled={starting}
                        />
                    </div>
                )}

                {failed && (
                    <p role="alert" className="mb-3 text-sm text-red-600">
                        {t("emdr.dock.startError")}
                    </p>
                )}

                <button
                    type="button"
                    onClick={() => void begin()}
                    disabled={!selected || starting}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent py-2.5 text-sm font-semibold text-accent-contrast transition-colors hover:bg-accent-hover disabled:opacity-50"
                >
                    {starting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                    {t("emdr.dock.start")}
                </button>

                <button
                    type="button"
                    onClick={close}
                    className="mt-2 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
                >
                    {t("common.cancel")}
                </button>
            </motion.div>
        </Modal>
    );
}
